/**
 * FRONT-3004: what one approval card says, read from the request.
 *
 * The server builds the card once, when EDDY asks, and stores it with the
 * request (`details.card`, `src/autonomy/approval-card.ts`), so chat, side
 * panel and phone read the same words. A request created before that (an
 * open request across a deploy) has no card; `cardOf` builds the closest
 * equivalent from its description so no screen ever renders a blank card or
 * falls back to the old layout.
 */
import type { Approval } from '~/hooks/queries/use-approvals';
import { humanizeToolName } from '~/lib/humanize-tool-name';
import { toolInputText } from '~/lib/redact-tool-input';
import { policyRuleLinkOf, policyRuleOf } from './policy-reason';

export type ApprovalCardKind = 'routine' | 'reaches_people' | 'visibility' | 'deletes';

export interface ApprovalCardFact {
  label: string;
  value: string;
}

export interface ApprovalCardPreview {
  label: string;
  text: string;
  /** Shown at once (an email body) or folded (a routine file's content). */
  open: boolean;
  mono?: boolean;
}

export interface ApprovalCardView {
  kind: ApprovalCardKind;
  title: string;
  subject?: string;
  why?: string;
  facts: ApprovalCardFact[];
  preview?: ApprovalCardPreview;
  confirmLabel: string;
  cancelLabel: string;
  /** Completes "EDDY asks before …". Empty: the footer names the Policy row instead, or is omitted. */
  askPhrase: string;
  /** The Policy row the card links to, or null for a card no row raised (an app visitor's). */
  policyRule: string | null;
  /** FRONT-2891: Policy opened on that row; null exactly when `policyRule` is. */
  policyLink: string | null;
  /** Which source wrote the headline; a tool's own card carries every fact, so it shows no raw arguments. */
  source: 'tool' | 'sentence' | 'connected' | 'fallback';
}

type Details = { card?: unknown; args?: unknown; tool?: string; domain?: string; action?: string };

function detailsOf(approval: Approval): Details {
  const d = approval.details;
  return d && typeof d === 'object' && !Array.isArray(d) ? (d as Details) : {};
}

const KINDS: ReadonlySet<string> = new Set(['routine', 'reaches_people', 'visibility', 'deletes']);
const SOURCES: ReadonlySet<string> = new Set(['tool', 'sentence', 'connected', 'fallback']);
const str = (v: unknown): string => (typeof v === 'string' ? v : '');

function isTerminalTool(tool: string | undefined): boolean {
  return tool === 'terminal_exec' || tool === 'terminal_session';
}

/** The stored card when it is well-formed; null otherwise. */
function storedCard(approval: Approval): Omit<ApprovalCardView, 'policyRule' | 'policyLink'> | null {
  const c = detailsOf(approval).card as Record<string, unknown> | undefined;
  if (!c || typeof c !== 'object' || c.v !== 1 || !str(c.title) || !KINDS.has(str(c.kind))) return null;
  const facts = Array.isArray(c.facts)
    ? (c.facts as unknown[]).flatMap((f) => {
        const r = f as { label?: unknown; value?: unknown };
        return str(r?.label) && str(r?.value) ? [{ label: str(r.label), value: str(r.value) }] : [];
      })
    : [];
  const p = c.preview as Record<string, unknown> | undefined;
  const preview =
    p && str(p.text)
      ? { label: str(p.label) || 'Preview', text: str(p.text), open: p.open === true, ...(p.mono === true ? { mono: true } : {}) }
      : undefined;
  return {
    kind: str(c.kind) as ApprovalCardKind,
    title: str(c.title),
    ...(str(c.subject) ? { subject: str(c.subject) } : {}),
    ...(str(c.why) ? { why: str(c.why) } : {}),
    facts,
    ...(preview ? { preview } : {}),
    confirmLabel: str(c.confirmLabel) || 'Yes, go ahead',
    cancelLabel: str(c.cancelLabel) || "No, don't",
    askPhrase: str(c.askPhrase),
    source: (SOURCES.has(str(c.source)) ? str(c.source) : 'fallback') as ApprovalCardView['source'],
  };
}

/** A request from before the card existed: the closest card its description allows. */
function legacyCard(approval: Approval): Omit<ApprovalCardView, 'policyRule' | 'policyLink'> {
  const { tool, action, domain, args } = detailsOf(approval);
  if (isTerminalTool(tool)) {
    const cmd = str((args as { cmd?: unknown } | undefined)?.cmd);
    return {
      kind: 'routine',
      title: 'Run a terminal command',
      // FRONT-2801's purpose was the description; an older row's raw tag is not a reason.
      ...(approval.description && !approval.description.startsWith(tool ?? '') ? { why: approval.description } : {}),
      facts: [],
      ...(cmd ? { preview: { label: 'Show command', text: cmd, open: false, mono: true } } : {}),
      confirmLabel: 'Run command',
      cancelLabel: "Don't run",
      askPhrase: '',
      source: 'sentence',
    };
  }
  const isSaveToMemory = domain === 'memory' && action !== 'remove_entity' && action !== 'overwrite_file';
  const destructive = /delete|remove|trash/.test(`${action ?? ''}`);
  const rawTagged = !!tool && approval.description.startsWith(tool);
  return {
    kind: destructive ? 'deletes' : 'routine',
    title: rawTagged ? humanizeToolName(tool) || 'Approve this action' : approval.description || 'Approve this action',
    facts: [],
    confirmLabel: isSaveToMemory ? 'Remember it' : destructive ? 'Delete' : 'Yes, go ahead',
    cancelLabel: destructive ? 'Keep it' : isSaveToMemory ? "Don't save" : "No, don't",
    askPhrase: '',
    source: 'fallback',
  };
}

export function cardOf(approval: Approval): ApprovalCardView {
  return {
    ...(storedCard(approval) ?? legacyCard(approval)),
    policyRule: policyRuleOf(approval),
    policyLink: policyRuleLinkOf(approval),
  };
}

/** The card as one plain line: the headline plus its subject. */
export function cardHeadline(card: Pick<ApprovalCardView, 'title' | 'subject'>): string {
  return card.subject ? `${card.title} ${card.subject}` : card.title;
}

/**
 * The call's own arguments, masked like the chat's tool card (FRONT-2716), for
 * the folded "Technical details". Null when the card's preview already shows
 * them (a terminal command) or there are none.
 */
export function technicalDetailsOf(approval: Approval, card: ApprovalCardView): string | null {
  const { args, tool } = detailsOf(approval);
  if (args == null || isTerminalTool(tool)) return null;
  if (typeof args === 'object' && !Array.isArray(args) && Object.keys(args).length === 0) return null;
  // A tool's own card names every fact itself (ids already turned into
  // names); its raw arguments would only put internal ids back on screen.
  if (card.source === 'tool' || card.preview?.mono) return null;
  try {
    return toolInputText(tool, args);
  } catch {
    return null;
  }
}
